import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {InfoPopover} from 'shells/billing/suite-header/info-popover';
import {Label} from 'shells/form/label';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';
import {Warning} from 'shells/warning/warning';

import {
    getErrorText,
    getUserCountText,
    getEngagementErrorText,
    getEngagementUserCountText,
    getDisabledUserCountText,
    getDisabledUserCount,
} from './helpers';

import styles from './num-users-input.module.css';

type Props = {
    numUsers: string;
    numEnabledUsers: number | null | undefined;
    numSalesUsers: string;
    numMarketingLicenses: number | null | undefined;
    numEnabledLicenses: number | null | undefined;
    isTrial: boolean;
    disabled: boolean;
    onNumUsersChange?: (numUsers: string) => void;
    suite: 'sales' | 'marketing' | 'engagement';
};

export function NumUsersInput(props: Props) {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);
    const textFieldRef = React.useRef(null);
    const errorTextSales = props.numEnabledUsers
        ? getErrorText(
              props.numEnabledUsers,
              props.numUsers ? props.numUsers : '',
              props.isTrial ? props.isTrial : false,
              props.numMarketingLicenses
          )
        : null;

    const errorTextEngagement = props.disabled
        ? null
        : getEngagementErrorText(props.numEnabledLicenses, props.numUsers ? props.numUsers : '');

    const userTextSales = props.numEnabledUsers
        ? getUserCountText(props.numEnabledUsers, props.numMarketingLicenses)
        : '';
    const userCountTextEngagement = getEngagementUserCountText(props.numEnabledLicenses);

    return (
        <div className='flex gap-8 align-center'>
            <Label htmlFor='numUsers' noBottomMargin={true}>
                Users
            </Label>
            <div
                ref={textFieldRef}
                onMouseEnter={() => setIsPopoverOpen(true)}
                onMouseLeave={() => setIsPopoverOpen(false)}
                className={styles['num-users-textfield']}
            >
                <TextField
                    id='numUsers'
                    value={
                        props.disabled
                            ? getDisabledUserCount(props.suite, Number(props.numSalesUsers))
                            : props.numUsers
                    }
                    onChange={props.onNumUsersChange}
                    type='number'
                    size='small'
                    min='0'
                    selectOnFocus={true}
                    hasError={
                        (props.suite === 'sales' && Boolean(errorTextSales)) ||
                        (props.suite === 'engagement' && Boolean(errorTextEngagement))
                    }
                    ignorePasswordAutoFill={true}
                    // @ts-expect-error
                    disabled={props.disabled}
                />
            </div>
            {props.disabled && isPopoverOpen && (
                <InfoPopover
                    anchor={textFieldRef.current}
                    text={getDisabledUserCountText(props.suite)}
                />
            )}
            {props.suite === 'sales' && Boolean(errorTextSales) && (
                <Warning text={errorTextSales} level='error' />
            )}
            {props.suite === 'engagement' && Boolean(errorTextEngagement) && (
                <Warning text={errorTextEngagement} level='error' />
            )}
            {!props.disabled && (
                <StackLegacy spacing={0}>
                    {props.suite === 'sales' && props.numEnabledUsers && (
                        <Body size='small' color='medium'>
                            {userTextSales}
                        </Body>
                    )}
                    {props.suite === 'engagement' && props.numEnabledLicenses && (
                        <Body size='small' color='medium'>
                            {userCountTextEngagement}
                        </Body>
                    )}
                    <Link
                        // @ts-expect-error
                        as={RoutedLink}
                        variant='primary'
                        to='/setup/user'
                        size='mini'
                        newTab={true}
                    >
                        Manage users
                    </Link>
                </StackLegacy>
            )}
        </div>
    );
}
